import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import api from '../services/api';
import html2canvas from 'html2canvas';
import { showToast } from '../context/ToastHandle';

const defaultCategories = ['general','core','food','school','home','social','emotions','actions','places','people','nature','travel','health','activities','time','weather'];
const languages = ['en'];
const ageRanges = ['all','2-4','4-7','8-12','teen','adult'];
const licenses = ['CC0'];

const backdropStyle = {
  position: 'fixed',
  top: 0, left: 0,
  width: '100vw',
  height: '100vh',
  background: 'rgba(0,0,0,0.45)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 10000,
  pointerEvents: 'auto',
  cursor: 'default',
};

const modalStyle = {
  width: 'min(680px, 92vw)',
  maxHeight: '88vh',
  overflow: 'auto',
  background: '#fff',
  borderRadius: '12px',
  boxShadow: '0 10px 30px rgba(0,0,0,0.2)',
  padding: '20px',
};

const rowStyle = { display: 'grid', gridTemplateColumns: '140px 1fr', gap: '12px', alignItems: 'start', marginBottom: '12px' };
const labelStyle = { fontWeight: 600, paddingTop: '8px' };
const inputStyle = { width: '90%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #ddd', fontSize: 14 };
const textareaStyle = { ...inputStyle, minHeight: 90, resize: 'vertical' };
const chipStyle = (active) => ({ padding: '6px 10px', borderRadius: '999px', border: `1px solid ${active ? '#2e7d32' : '#bbb'}`, background: active ? '#eaf7ec' : '#fff', cursor: 'pointer', fontSize: 13 });

export default function ShareBoardButton({ boardId, boardName, containerRef, onShared }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const [title, setTitle] = useState(boardName || 'My Board');
  const [description, setDescription] = useState('');
  const [tagsCsv, setTagsCsv] = useState('');
  const [language, setLanguage] = useState('en');
  const [ageRange, setAgeRange] = useState('all');
  const [selectedCategories, setSelectedCategories] = useState(['general']);
  const [licenseCode] = useState('CC0'); // currently fixed to CC0

  const firstInputRef = useRef(null);

  // Lock body scroll while modal is open
  useEffect(() => {
    if (open) document.body.classList.add('modal-open');
    else document.body.classList.remove('modal-open');
    return () => document.body.classList.remove('modal-open');
  }, [open]);

  useEffect(() => { if (open) setTimeout(() => firstInputRef.current?.focus(), 50); }, [open]);
  useEffect(() => { setTitle(boardName || 'My Board'); }, [boardName]);

  const canSubmit = useMemo(() => !!boardId && title.trim().length >= 2 && !loading, [boardId, title, loading]);

  const toggleCategory = (cat) =>
    setSelectedCategories((prev) => (prev.includes(cat) ? prev.filter((c) => c !== cat) : [...prev, cat]));

  // ---------- Thumbnail helpers (works from BoardView OR BoardList) ----------

  async function preloadImages(urls = []) {
    const uniques = Array.from(new Set(urls.filter(Boolean)));
    await Promise.all(
      uniques.map(
        (src) =>
          new Promise((resolve) => {
            const img = new Image();
            img.crossOrigin = 'anonymous';
            img.onload = img.onerror = resolve;
            img.src = src;
            if (img.complete) resolve();
          })
      )
    );
  }

  async function getBoardConfig(boardId) {
  // Adjust to your actual endpoint that returns grid_config for a board
  const { data } = await api.get(`/boards.php?id=${boardId}`);
  // Expecting shape: { success: true, data: { grid_config: '{"columns":8,"rows":5}', ... } }
  const cfgStr = data?.data?.grid_config || data?.grid_config || null;
  if (!cfgStr) return { columns: 8, rows: 5 };
  try {
    const cfg = JSON.parse(cfgStr);
    return { columns: Number(cfg.columns) || 8, rows: Number(cfg.rows) || 5 };
  } catch {
    return { columns: 8, rows: 5 };
  }
}

  async function captureOffscreenThumb(boardId) {
    try {
      // 1) Fetch tiles for the board (adjust if your endpoint differs)
        const { data } = await api.get(`/tiles.php?board_id=${boardId}`);
        const tiles = Array.isArray(data) ? data.sort((a, b) => (a.position || 0) - (b.position || 0)) : [];
        if (!tiles.length) return null;

        const cfg = await getBoardConfig(boardId);
        const TILE_SIZE = 100;
        const GAP = 10;
        const columns = Math.max(1, Math.min(12, cfg.columns || 8));

        // 2) Preload tile images so html2canvas draws them
        await preloadImages(tiles.map(t => (t.image_url || '').trim()).filter(Boolean));

        // 3) Create offscreen wrapper
        const wrapper = document.createElement('div');
        wrapper.style.position = 'fixed';
        wrapper.style.left = '-99999px';
        wrapper.style.top = '0';
        wrapper.style.background = '#fff';
        wrapper.style.padding = '12px';
        wrapper.style.boxSizing = 'border-box';
        wrapper.style.width = `${columns * TILE_SIZE + (columns - 1) * GAP + 24}px`;
        document.body.appendChild(wrapper);

        const grid = document.createElement('div');
        grid.style.display = 'grid';
        grid.style.gridTemplateColumns = `repeat(${columns}, ${TILE_SIZE}px)`;
        grid.style.columnGap = `${GAP}px`;
        grid.style.rowGap = `${GAP}px`;
        grid.style.justifyContent = 'start';
        wrapper.appendChild(grid);

        tiles.forEach((t) => {
            const cell = document.createElement('div');
            cell.style.width = `${TILE_SIZE}px`;
            cell.style.height = `${TILE_SIZE}px`;
            cell.style.borderRadius = t.is_folder ? '8px 80px 8px 8px' : '8px';
            cell.style.border = '1px solid #ccc';
            cell.style.background = '#eee';
            cell.style.display = 'flex';
            cell.style.flexDirection = 'column';
            cell.style.alignItems = 'center';
            cell.style.justifyContent = 'center';
            cell.style.overflow = 'hidden';

            if (t.image_url) {
            const img = document.createElement('img');
            img.src = t.image_url;
            img.alt = t.label || '';
            img.style.width = '48px';
            img.style.height = '48px';
            img.style.objectFit = 'contain';
            img.style.marginBottom = '4px';
            img.crossOrigin = 'anonymous';
            cell.appendChild(img);
            }
            const span = document.createElement('span');
            span.textContent = t.label || '';
            span.style.fontSize = '12px';
            span.style.fontWeight = '600';
            span.style.textAlign = 'center';
            cell.appendChild(span);

            grid.appendChild(cell);
      });

      // 5) Capture
      const canvas = await html2canvas(wrapper, {
        backgroundColor: '#ffffff',
        useCORS: true,
        allowTaint: true,
        scale: 1,
        logging: false,
        windowWidth: wrapper.offsetWidth,
        windowHeight: wrapper.offsetHeight,
      });

      // 6) Cleanup
      document.body.removeChild(wrapper);

      // 7) Compress
      return canvas.toDataURL('image/jpeg', 0.85);
    } catch (e) {
      console.warn('Offscreen capture failed:', e);
      return null;
    }
  }

  async function captureThumb() {
    // Prefer live capture if used on BoardView
    if (containerRef?.current) {
      try {
        const el = containerRef.current;
        const prevOverflow = el.style.overflow;
        el.style.overflow = 'hidden';
        const canvas = await html2canvas(el, {
          backgroundColor: '#ffffff',
          useCORS: true,
          allowTaint: true,
          scale: 0.9,
          logging: false,
          windowWidth: el.scrollWidth,
          windowHeight: el.scrollHeight,
        });
        el.style.overflow = prevOverflow;
        return canvas.toDataURL('image/jpeg', 0.8);
      } catch (e) {
        console.warn('Live capture failed, falling back to offscreen.', e);
      }
    }
    // Fallback for BoardList
    return await captureOffscreenThumb(boardId);
  }

  // --------------------------------------------------------------------------

  async function handleSubmit(e) {
    e?.preventDefault?.();
    if (!canSubmit) return;

    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      if (!token) {
        showToast('You must be logged in to share.', 'error', false);
        return;
      }

      const thumb_data_url = await captureThumb();

      const payload = {
        root_board_id: boardId,
        title: title.trim(),
        description: description.trim(),
        tags_csv: tagsCsv.trim(),
        language,
        age_range: ageRange,
        categories: selectedCategories,
        license_code: licenseCode,
        ...(thumb_data_url ? { thumb_data_url } : {}),
      };

      const res = await api.post('/community_publish.php', payload, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });

      if (res.data?.success) {
        showToast('Board shared with the community! 🎉', 'success', false);
        setOpen(false);
        setDescription(''); setTagsCsv(''); setSelectedCategories(['general']);
        onShared?.(res.data.data);
      } else {
        showToast(res.data?.error || 'Failed to share board.', 'error', false);
      }
    } catch (err) {
      console.error(err);
      showToast('Error sharing board.', 'error', false);
    } finally {
      setLoading(false);
    }
  }

  // Render modal via portal so it's outside BoardList's stacking/overflow context
  const modal = open ? createPortal(
    <div
      style={backdropStyle}
      onClick={() => setOpen(false)}
      onMouseDown={(e) => { e.stopPropagation(); }}
      onDragStart={(e) => { e.preventDefault(); e.stopPropagation(); }}
      onPointerDown={(e) => { e.stopPropagation(); }}
    >
      <div
        style={modalStyle}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
          <h3 style={{ margin: 0 }}>Share “{boardName || 'Board'}”</h3>
          <button
            aria-label="Close"
            onClick={() => setOpen(false)}
            style={{ background: 'transparent', border: 'none', fontSize: 22, cursor: 'pointer', lineHeight: 1 }}
          >
            ×
          </button>
        </div>

        <p style={{ marginTop: 0, color: '#555' }}>
          Publish this board to the Community so others can discover and import it. Sharing your boards will share symbols and images too, so please make sure you have the rights to share everything on this board.
        </p>

        <form onSubmit={handleSubmit}>
          <div style={rowStyle}>
            <label style={labelStyle}>Title *</label>
            <input
              ref={firstInputRef}
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g., Core Words Starter"
              style={inputStyle}
              required
              minLength={2}
            />
          </div>

          <div style={rowStyle}>
            <label style={labelStyle}>Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What’s inside? Who is it for?"
              style={textareaStyle}
            />
          </div>

          <div style={rowStyle}>
            <label style={labelStyle}>Tags</label>
            <input
              type="text"
              value={tagsCsv}
              onChange={(e) => setTagsCsv(e.target.value)}
              placeholder="comma,separated,tags"
              style={inputStyle}
            />
          </div>

          <div style={rowStyle}>
            <label style={labelStyle}>Language</label>
            <select value={language} onChange={(e) => setLanguage(e.target.value)} style={inputStyle}>
              {languages.map((l) => <option key={l} value={l}>{l}</option>)}
            </select>
          </div>

          <div style={rowStyle}>
            <label style={labelStyle}>Age range</label>
            <select value={ageRange} onChange={(e) => setAgeRange(e.target.value)} style={inputStyle}>
              {ageRanges.map((a) => <option key={a} value={a}>{a}</option>)}
            </select>
          </div>

          <div style={rowStyle}>
            <label style={labelStyle}>Categories</label>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {defaultCategories.map((cat) => (
                <span key={cat} style={chipStyle(selectedCategories.includes(cat))} onClick={() => {
                  setSelectedCategories((prev) => prev.includes(cat) ? prev.filter((c) => c !== cat) : [...prev, cat]);
                }}>
                  {cat}
                </span>
              ))}
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 18 }}>
            <button
              type="button"
              onClick={() => setOpen(false)}
              style={{ background: '#f3f4f6', color: '#111', border: '1px solid #ddd', borderRadius: 8, padding: '10px 14px', cursor: 'pointer' }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!canSubmit}
              style={{ background: canSubmit ? '#4CAF50' : '#a5d6a7', color: 'white', border: 'none', borderRadius: 8, padding: '10px 16px', cursor: canSubmit ? 'pointer' : 'not-allowed', fontWeight: 600 }}
            >
              {loading ? 'Sharing…' : 'Share'}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  ) : null;

  return (
    <>
      <button
        className="share-board-btn"
        onClick={() => setOpen(true)}
        style={{ backgroundColor: '#4CAF50', color: 'white', border: 'none', borderRadius: '6px', padding: '8px 14px', cursor: 'pointer', fontSize: '0.9rem' }}
      >
        Share Board
      </button>
      {modal}
    </>
  );
}
